import {
  MODULES,
  MODULE_LIST,
  SYSTEM_IDS,
  type ModuleId,
  type SystemId,
} from './modules';

/**
 * Qué se puede encender, y por qué no.
 *
 * Los **sistemas** son lo que la organización contrata de Ribox: telefonía y
 * WhatsApp. De ellos cuelga todo lo demás — sin telefonía no hay módulo
 * Llamadas que valga, porque detrás no hay dialer que llame. Por eso no es
 * «apagado»: apagado se revierte con un clic, y esto no se puede encender.
 *
 * Vive acá y no dentro de la pantalla porque es la regla, no el dibujo: la
 * misma respuesta la da el servidor con un 422, y las dos tienen que decir lo
 * mismo. Se prueba sin montar nada, igual que `resolveEnabledModules`.
 */

export type Systems = Record<SystemId, boolean>;

/** Sólo para lo que lee una persona; los identificadores siguen en inglés. */
export const SYSTEM_LABELS: Record<SystemId, string> = {
  phone: 'Telefonía',
  whatsapp: 'WhatsApp',
};

export const SYSTEM_DESCRIPTIONS: Record<SystemId, string> = {
  phone: 'El agente de voz: llamadas salientes, grabación y su registro en el CRM.',
  whatsapp: 'Los agentes de texto: conversaciones de WhatsApp y web dentro del CRM.',
};

/**
 * **Sin `systems` escrito, la organización tiene todos los sistemas.**
 *
 * Es lo contrario de lo que un campo nuevo suele significar, y es a propósito.
 * Hoy ningún documento lo trae: si la ausencia significara «no contrata nada»,
 * el despliegue le apagaría Llamadas y Bandeja a todas las organizaciones a la
 * vez —incluidas las que llaman por teléfono todos los días— sin que nadie
 * hubiera tocado un interruptor. Así, la ausencia deja todo como estaba y
 * escribir el campo es un acto explícito, uno por organización.
 *
 * El parámetro admite `undefined` porque el frontend y la API se despliegan por
 * separado: entre un despliegue y el otro la respuesta no trae el campo
 * todavía, aunque el tipo diga que sí.
 */
export function resolveSystems(stored?: Partial<Systems> | null): Systems {
  const resolved = {} as Systems;
  for (const system of SYSTEM_IDS) {
    const held = stored?.[system];
    resolved[system] = typeof held === 'boolean' ? held : true;
  }
  return resolved;
}

/** Los módulos que dependen de este sistema, en el orden del registro. */
export function modulesNeeding(system: SystemId): ModuleId[] {
  return MODULE_LIST.filter((module) => module.requiresSystem === system).map(
    (module) => module.id,
  );
}

/**
 * Por qué este módulo no se puede encender, o `null` si sí se puede.
 *
 * Devuelve el motivo y no un booleano porque un interruptor deshabilitado sin
 * explicación es un acertijo: la pantalla necesita la frase, y tenerla acá es
 * lo que hace que sea la misma en todas partes.
 */
export function moduleBlockedReason(id: ModuleId, systems: Systems): string | null {
  const system = MODULES[id].requiresSystem;
  if (system === undefined || systems[system]) return null;
  return `Necesita el sistema de ${SYSTEM_LABELS[system]}, que esta organización no tiene contratado.`;
}

export function canEnableModule(id: ModuleId, systems: Systems): boolean {
  return moduleBlockedReason(id, systems) === null;
}

/**
 * Por qué este sistema no se puede apagar todavía, o `null` si sí se puede.
 *
 * El otro sentido de la misma regla. No se arrastra el módulo hacia abajo —una
 * cascada silenciosa esconde la decisión— así que apagar el sistema exige
 * apagar antes lo que cuelga de él, y esto dice qué es.
 */
export function systemBlockedReason(
  system: SystemId,
  enabledModules: readonly ModuleId[],
): string | null {
  const enabled = new Set(enabledModules);
  const holding = modulesNeeding(system).filter((module) => enabled.has(module));
  if (holding.length === 0) return null;
  const names = holding.map((module) => MODULES[module].label).join(', ');
  return `Apaga primero ${names}: sin este sistema no funciona.`;
}
